import { formatPrice } from '@repo/shared/utils/priceFormatter';
import type { BaseFareSummary, BookingStatus, CabPricing } from '@/api';
import type { FareSummaryRowItem } from '../FareSummary';

export interface PartialPayFareRowsResult {
	/** Heading row(s) for the paid-charges group ("Total Paid" / "Total Amount") */
	paidCharges: BaseFareSummary[];
	/** Row(s) for the to-be-paid group (initial + remaining for partial-pay) */
	toBePaidCharges: FareSummaryRowItem[];
}

/**
 * Builds the fare-summary paid / to-be-paid rows for a trip-detail card,
 * branching on whether the booking is partial-pay.
 *
 * - Partial-pay (initialPayAmount + payLaterAmount both present):
 *     paidCharges → single sum row with `fareHeadingLabel` ("Total Amount" /
 *     "Total Paid" depending on state).
 *     toBePaidCharges → "Initial Amount Paid" + a second row that flips
 *     between "Remaining amount to be paid" (warning-bold, pre-completion)
 *     and "Amount paid to driver" (standard styling, post-completion).
 *
 * - Non-partial-pay: paidCharges → single `displayPrice` row;
 *     toBePaidCharges → falls back to `amountToBePaid` if provided.
 */
export function getPartialPayFareRows(
	cabPricing: CabPricing,
	bookingStatus: BookingStatus,
	fallback?: { amountToBePaid?: BaseFareSummary },
): PartialPayFareRowsResult {
	const {
		initialPayAmount,
		payLaterAmount,
		currency,
		displayPrice,
		fareHeadingLabel,
	} = cabPricing;

	const isPartialPay =
		initialPayAmount !== undefined && payLaterAmount !== undefined;
	const isCompleted = bookingStatus === 'COMPLETED';
	const headingLabel = fareHeadingLabel ?? 'Total Paid';

	const paidCharges: BaseFareSummary[] = isPartialPay
		? [
				{
					label: headingLabel,
					value: formatPrice((initialPayAmount ?? 0) + (payLaterAmount ?? 0), {
						currency,
					}),
				},
			]
		: displayPrice !== undefined
			? [
					{
						label: headingLabel,
						value: formatPrice(displayPrice, { currency }),
					},
				]
			: [];

	const toBePaidCharges: FareSummaryRowItem[] = isPartialPay
		? [
				{
					label: 'Initial Amount Paid',
					value: formatPrice(initialPayAmount ?? 0, { currency }),
					size: 'md',
				},
				{
					label: isCompleted
						? 'Amount paid to driver'
						: 'Remaining amount to be paid',
					value: formatPrice(payLaterAmount ?? 0, { currency }),
					size: 'md',
					// Pre-completion: warning-bold styling to draw attention to
					// the still-owed amount. Post-completion: standard styling
					// since it's no longer outstanding.
					...(isCompleted
						? {}
						: {
								labelColor: 'text-warning',
								valueColor: 'text-warning',
								labelWeight: 'bold',
								valueWeight: 'bold',
							}),
				},
			]
		: fallback?.amountToBePaid
			? [
					{
						label: fallback.amountToBePaid.label || '',
						value: fallback.amountToBePaid.value || '',
					},
				]
			: [];

	return { paidCharges, toBePaidCharges };
}
